import './GenerationStatusDialog.scss';
import { ReactNode } from 'react';
import { AlertDialog } from '../../Components/AlertDialog';
import { Design, Intent, IntentType } from '../../Components/designConstants';
import { Spinner } from '../../Components/Spinner';
import { conditionallyPluralizeWithCount } from '../../utils/text-utils';
import {
  GenerationDialogState,
  GenerationDialogStateType,
  PresentationStatus
} from './presentation-status-constants';
import { getGenerationDialogState } from './presentation-status-utils';
import { usePresentationStatus } from './usePresentationStatus';

const SLACK_FALLBACK_TEXT =
  "Your presentation is generating. You can navigate away from this page — we'll send you a Slack message when it's ready.";
const IN_PROGRESS_SUFFIX =
  "Keep this dialog open to get the link here, or navigate away — we'll send you a Slack message when it's ready.";

interface GenerationStatusDialogProps {
  jobId: string | undefined;
  onHide: () => void;
}

// The dialog after Export. With a jobId it polls the job and shows the deck link or the error;
// without one it keeps the Slack message text.
export const GenerationStatusDialog = ({ jobId, onHide }: GenerationStatusDialogProps) => {
  if (!jobId) {
    return (
      <StatusDialog state={GenerationDialogState.NO_JOB_ID} status={undefined} onHide={onHide} />
    );
  }
  return <PolledStatusDialog jobId={jobId} onHide={onHide} />;
};

interface PolledStatusDialogProps {
  jobId: string;
  onHide: () => void;
}

const PolledStatusDialog = ({ jobId, onHide }: PolledStatusDialogProps) => {
  const { status, hasError, pollLimitPassed } = usePresentationStatus(jobId);
  const state = getGenerationDialogState({ jobId, status, hasError, pollLimitPassed });
  return <StatusDialog state={state} status={status} onHide={onHide} />;
};

interface StatusDialogProps {
  state: GenerationDialogStateType;
  status: PresentationStatus | undefined;
  onHide: () => void;
}

interface StatusDialogContent {
  intent: IntentType;
  title: string;
  icon: ReactNode;
  body: ReactNode;
}

const getStatusDialogContent = (
  state: GenerationDialogStateType,
  status: PresentationStatus | undefined
): StatusDialogContent => {
  switch (state) {
    case GenerationDialogState.QUEUED:
      return {
        intent: Intent.SUCCESS,
        title: 'Generation in Progress',
        icon: <Spinner size={20} />,
        body: `Your presentation is queued. ${IN_PROGRESS_SUFFIX}`
      };
    case GenerationDialogState.RUNNING:
      return {
        intent: Intent.SUCCESS,
        title: 'Generation in Progress',
        icon: <Spinner size={20} />,
        body: `Your presentation is generating. ${IN_PROGRESS_SUFFIX}`
      };
    case GenerationDialogState.SUCCEEDED:
      return {
        intent: Intent.SUCCESS,
        title: 'Presentation Ready',
        icon: null,
        body: <SucceededBody status={status} />
      };
    case GenerationDialogState.FAILED:
      return {
        intent: Intent.ERROR,
        title: 'Generation Failed',
        icon: null,
        body: <FailedBody lead='Your presentation could not be generated.' status={status} />
      };
    case GenerationDialogState.EXPIRED:
      return {
        intent: Intent.ERROR,
        title: 'Generation Timed Out',
        icon: null,
        body: <FailedBody lead='Your presentation did not finish in time.' status={status} />
      };
    default:
      return {
        intent: Intent.SUCCESS,
        title: 'Generation in Progress',
        icon: null,
        body: SLACK_FALLBACK_TEXT
      };
  }
};

const StatusDialog = ({ state, status, onHide }: StatusDialogProps) => {
  const { intent, title, icon, body } = getStatusDialogContent(state, status);
  return (
    <AlertDialog
      intent={intent}
      show
      onHide={onHide}
      icon={icon}
      title={title}
      body={body}
      primaryAction={{ label: 'Close', design: Design.NEUTRAL }}
      dataTestId='generation-status-dialog'
    />
  );
};

interface StatusBodyProps {
  status: PresentationStatus | undefined;
}

const SucceededBody = ({ status }: StatusBodyProps) => {
  const warningCount = status?.warnings.length ?? 0;
  return (
    <div className='generation-status-body'>
      <div>Your presentation is ready.</div>
      {status?.presentationUrl && (
        <a
          href={status.presentationUrl}
          target='_blank'
          rel='noreferrer'
          data-testid='presentation-link'
        >
          Open presentation
        </a>
      )}
      {warningCount > 0 && (
        <div data-testid='presentation-warnings'>
          {`${conditionallyPluralizeWithCount(warningCount, 'warning')}: some slide content could not be updated.`}
        </div>
      )}
    </div>
  );
};

interface FailedBodyProps extends StatusBodyProps {
  lead: string;
}

const FailedBody = ({ lead, status }: FailedBodyProps) => (
  <div className='generation-status-body'>
    <div>{lead}</div>
    {status?.errorMessage && (
      <div data-testid='generation-error-message'>{status.errorMessage}</div>
    )}
    {status?.errorCode && (
      <div data-testid='generation-error-code'>{`Error code: ${status.errorCode}`}</div>
    )}
  </div>
);
